import {Injectable} from '@angular/core';
import {FaultField} from '../mm-fault-detail-form/fault-field';
import {FieldConfig} from '@mydocma/dynamic-form/lib/dynamic-field/field.config';
import {FaultFieldTypeEnum} from '../mm-fault-detail-form/fault-field-type.enum';
import {DateFieldConfig, FieldTypeEnum, FilterSelectFieldConfig, NumberFieldConfig, NumberTypeEnum, SelectItem, SlideToggleFieldConfig, TextAreaFieldConfig, TextFieldConfig} from '@mydocma/dynamic-form';
import {FieldPermission} from '@docma-mm-core/field/field-permission.enum';
import {firstValueFrom, Observable} from 'rxjs';
import {FieldAffix} from '@mydocma/dynamic-form/lib/affix/field-affix';
import {FieldPropertyEnum} from '@docma-mm-core/field/field-property.enum';
import {ProjectActionPermissionEnum} from '@docma-mm-core/permission/project-action-permission.enum';
import {MmProjectPermissionsService} from '../../../mm-project/mm-project-permissions-service';
import {filter, map} from 'rxjs/operators';
import {LoadStatusEnum} from '@docma-mm-core/model/load-state/load-state.enum';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import {MmProjectLoaderService} from '../../../mm-project/mm-project-loader.service';
import {getCompanyInfo} from '@docma-mm-core/company/company';
import {getOrderInfo} from '@docma-mm-core/order/order';
import {TranslateService} from '@ngx-translate/core';
import {Logger} from '@mydocma/common/src/lib/logger';
import {flattenStructure, Structure} from '@docma-mm-core/structure/structure';
import {FaultData} from '@docma-mm-core/fault/fault-data';
import {FieldConfig as FaultFieldConfig} from '@docma-mm-core/field-config/field-config';
import {constants} from '@docma-mm-core/constants';

@UntilDestroy()
@Injectable()
export class MmFaultFieldService {

  fieldSettings: { [name: string]: { maxLength: number, fieldType: FaultFieldTypeEnum } } = {
    [FieldPropertyEnum.Number2]: {maxLength: 64, fieldType: FaultFieldTypeEnum.Text},
    [FieldPropertyEnum.Desc1_1]: {maxLength: 4000, fieldType: FaultFieldTypeEnum.TextArea},
    [FieldPropertyEnum.Desc1_2]: {maxLength: 4000, fieldType: FaultFieldTypeEnum.TextArea},
    [FieldPropertyEnum.Desc1_3]: {maxLength: 4000, fieldType: FaultFieldTypeEnum.TextArea},
    [FieldPropertyEnum.Desc2_1]: {maxLength: 4000, fieldType: FaultFieldTypeEnum.TextArea},
    [FieldPropertyEnum.Desc2_2]: {maxLength: 4000, fieldType: FaultFieldTypeEnum.TextArea},
    [FieldPropertyEnum.Desc2_3]: {maxLength: 4000, fieldType: FaultFieldTypeEnum.TextArea},
    [FieldPropertyEnum.Text1]: {maxLength: 4000, fieldType: FaultFieldTypeEnum.TextArea},
    [FieldPropertyEnum.Text2]: {maxLength: 4000, fieldType: FaultFieldTypeEnum.TextArea},
    [FieldPropertyEnum.Text3]: {maxLength: 4000, fieldType: FaultFieldTypeEnum.TextArea},
  }

  structures$ = this.projectLoaderService.structures.pipe(
    filter(m => m?.loadState?.status === LoadStatusEnum.Loaded),
    map(m => flattenStructure(m.data).map(structure => this.toStructureItem(structure))),
    untilDestroyed(this));

  crafts$: Observable<SelectItem[]> = this.projectLoaderService.crafts.pipe(
    filter(m => m?.loadState?.status === LoadStatusEnum.Loaded),
    map(m => m.data.map(c => ({id: c.Id, text: c.Name1 + (c.Name2 ? ' - ' + c.Name2 : '')}))),
    untilDestroyed(this));

  userDefineds1$: Observable<SelectItem[]> = this.projectLoaderService.userDefineds.pipe(
    filter(m => m?.loadState?.status === LoadStatusEnum.Loaded),
    map(m => (m.data?.Type1 ? m.data.Type1.map(u => ({id: u.Id, text: u.Name1 + (u.Name2 ? ' - ' + u.Name2 : '')})) : [])),
    untilDestroyed(this));

  userDefineds2$: Observable<SelectItem[]> = this.projectLoaderService.userDefineds.pipe(
    filter(m => m?.loadState?.status === LoadStatusEnum.Loaded),
    map(m => (m.data?.Type2 ? m.data.Type2.map(u => ({id: u.Id, text: u.Name1 + (u.Name2 ? ' - ' + u.Name2 : '')})) : [])),
    untilDestroyed(this));

  class1$: Observable<SelectItem[]> = this.projectLoaderService.classifications.pipe(
    filter(m => m?.loadState?.status === LoadStatusEnum.Loaded),
    map(m => (m.data?.Class1 ? m.data.Class1.map(c => ({id: c.Id, text: c.Name1 + (c.Name2 ? ' - ' + c.Name2 : '')})) : [])),
    untilDestroyed(this));

  class2$: Observable<SelectItem[]> = this.projectLoaderService.classifications.pipe(
    filter(m => m?.loadState?.status === LoadStatusEnum.Loaded),
    map(m => (m.data?.Class2 ? m.data.Class2.map(c => ({id: c.Id, text: c.Name1 + (c.Name2 ? ' - ' + c.Name2 : '')})) : [])),
    untilDestroyed(this));

  class3$: Observable<SelectItem[]> = this.projectLoaderService.classifications.pipe(
    filter(m => m?.loadState?.status === LoadStatusEnum.Loaded),
    map(m => (m.data?.Class3 ? m.data.Class3.map(c => ({id: c.Id, text: c.Name1 + (c.Name2 ? ' - ' + c.Name2 : '')})) : [])),
    untilDestroyed(this));

  class4$: Observable<SelectItem[]> = this.projectLoaderService.classifications.pipe(
    filter(m => m?.loadState?.status === LoadStatusEnum.Loaded),
    map(m => (m.data?.Class4 ? m.data.Class4.map(c => ({id: c.Id, text: c.Name1 + (c.Name2 ? ' - ' + c.Name2 : '')})) : [])),
    untilDestroyed(this));

  companies$: Observable<SelectItem[]> = this.projectLoaderService.companies.pipe(
    // switchMap(companies => this.currentFault.pipe(map(fault => ({companies, fault})))),
    filter(companies => companies?.loadState?.status === LoadStatusEnum.Loaded),
    map(companies => companies.data.filter(c => (c.IsDeleted === false && c.IsActive === true && c.IsInsolvent === false) || c.Id === this.currentFault?.Company?.Id)
      .map(c => ({id: c.Id, text: c.ShortName + (c.Name1 ? ' - ' + c.Name1 : '') + getCompanyInfo(c, this.translateService)}))),
    untilDestroyed(this));

  costCompanies$: Observable<SelectItem[]> = this.projectLoaderService.companies.pipe(
    // switchMap(companies => this.currentFault.pipe(map(fault => ({companies, fault})))),
    filter(companies => companies?.loadState?.status === LoadStatusEnum.Loaded),
    map(companies => companies.data.filter(c => (c.IsDeleted === false && c.IsActive === true && c.IsInsolvent === false) || c.Id === this.currentFault?.CostsCompany?.Id)
      .map(c => ({id: c.Id, text: c.ShortName + (c.Name1 ? ' - ' + c.Name1 : '') + getCompanyInfo(c, this.translateService)}))),
    untilDestroyed(this));

  orders$: Observable<SelectItem[]> = this.projectLoaderService.orders.pipe(
    // switchMap(orders => this.currentFault.pipe(map(fault => ({orders, fault})))),
    filter(orders => orders?.loadState?.status === LoadStatusEnum.Loaded),
    map(orders => orders.data.filter(o => (o.IsDeleted === false && o.IsActive === true && o.Company.IsActive === true && o.Company.IsDeleted === false && (o.Company?.IsInsolvent ?? false) === false) || o.Id === this.currentFault?.Order?.Id)
      .map(o => ({
        id: o.Id,
        text: o.Description1 + (o.Description2 ? ' - ' + o.Description2 : '') + getOrderInfo(o, o.Company, this.translateService)
      }))),
    untilDestroyed(this));

  costOrders$: Observable<SelectItem[]> = this.projectLoaderService.orders.pipe(
    // switchMap(orders => this.currentFault.pipe(map(fault => ({orders, fault})))),
    filter(orders => orders?.loadState?.status === LoadStatusEnum.Loaded),
    map(orders => orders.data.filter(o => (o.IsDeleted === false && o.IsActive === true && o.Company.IsActive === true && o.Company.IsDeleted === false && (o.Company?.IsInsolvent ?? false) === false) || o.Id === this.currentFault?.CostsOrder?.Id)
      .map(o => ({
        id: o.Id,
        text: o.Description1 + (o.Description2 ? ' - ' + o.Description2 : '') + getOrderInfo(o, o.Company, this.translateService)
      }))),
    untilDestroyed(this));

  permissionEditAssignedStructureOfFault$ = this.permissionService.hasProjectActionPermissions([ProjectActionPermissionEnum.EditAssignedStructureOfFault]);

  constructor(private readonly logger: Logger,
              private readonly permissionService: MmProjectPermissionsService,
              public readonly projectLoaderService: MmProjectLoaderService,
              private readonly translateService: TranslateService) {
  }

  currentFault: FaultData;

  getCurrentStructureItem(structureId: number) {
    for (const root of this.projectLoaderService.getCurrentStructures()) {
      const structure = root.findById(structureId);
      if (structure) return this.toStructureItem(structure);
    }
    return null;
  }

  whenStructuresLoaded() {
    return firstValueFrom(this.projectLoaderService.structures.pipe(filter(m => m?.loadState?.status === LoadStatusEnum.Loaded)));
  }

  private toStructureItem(structure: Structure) {
    return {id: structure.Id, text: structure.Name1 + (structure.Name2 ? ' - ' + structure.Name2 : ''), data: structure.StructurePath};
  }

  async getFieldConfigs(fields: FaultField[], fieldConfigsDict: {[prop: string]: FaultFieldConfig}, noDataSourceFields: string[], companyDependencyEnabled: boolean, hasPermissionEditFaults: boolean, openStructureSelectionAction: () => {}, showStructurePathField = false) {
    const formConfigs: FieldConfig<string>[] = [];
    // console.warn('MmFaultFieldService.getFieldConfigs.0')
    // console.warn(fields);

    for (const field of fields) {
      try {
        let config: any;
        let config2: any;

        switch (field.Type) {
          case FaultFieldTypeEnum.TextArea:
            const textAreaFieldConfig: TextAreaFieldConfig = {
              id: field.Id,
              name: field.Prop,
              label: {
                text: field.Name,
                tooltip: ''
              },
              type: FieldTypeEnum.textarea,
              maxLength: (this.fieldSettings.hasOwnProperty(field.Prop) ? this.fieldSettings[field.Prop].maxLength : null),
              required: (field.Permission === FieldPermission.Mandatory),
              readonly: !(field.Permission === FieldPermission.Write || field.Permission === FieldPermission.Mandatory) || !hasPermissionEditFaults,
              autosize: true,
              rows: 1,
              minRows: 1,
              maxRows: 10,
              class: (fieldConfigsDict.hasOwnProperty(field.Prop) && fieldConfigsDict[field.Prop]?.Memory == true ? 'memory-field' : null)
            };
            config = textAreaFieldConfig;
            // console.warn('MmFaultFieldService.getFieldConfigs.1.1')
            // console.warn(config);

            break;
          case FaultFieldTypeEnum.Structure:
            const structureFieldConfig: TextFieldConfig = {
              id: field.Id,
              name: field.Prop,
              label: {
                text: field.Name,
                tooltip: ''
              },
              type: FieldTypeEnum.text,
              maxLength: (this.fieldSettings.hasOwnProperty(field.Prop) ? this.fieldSettings[field.Prop].maxLength : null),
              required: true,
              readonly: true,
              showClearButtonIfReadonly: false,
              suffix:  ({
                icon: 'account_tree',
                readonly: false,
                click: openStructureSelectionAction
              } as FieldAffix)
            };
            config = structureFieldConfig;

            if (showStructurePathField) {
              const structurePathFieldConfig: TextAreaFieldConfig = {
                id: 'StructurePath',
                name: 'StructurePath',
                label: {
                  text: '',
                  tooltip: ''
                },
                type: FieldTypeEnum.textarea,
                readonly: true,
                autosize: true,
                maxLength: 4000,
                rows: 1,
                minRows: 1,
                maxRows: constants.MaxInt,
                class: 'mydocma-ignore-readonly'
              };
              config2 = structurePathFieldConfig;
            }

            break;

          case FaultFieldTypeEnum.Label:
          case FaultFieldTypeEnum.Text:
          // case FaultFieldTypeEnum.Structure:
            const textFieldConfig: TextFieldConfig = {
              id: field.Id,
              name: field.Prop,
              label: {
                text: field.Name,
                tooltip: ''
              },
              type: FieldTypeEnum.text,
              maxLength: (this.fieldSettings.hasOwnProperty(field.Prop) ? this.fieldSettings[field.Prop].maxLength : null),
              required: (field.Permission === FieldPermission.Mandatory),
              readonly: !(field.Permission === FieldPermission.Write || field.Permission === FieldPermission.Mandatory) || !hasPermissionEditFaults,
              class: (fieldConfigsDict.hasOwnProperty(field.Prop) && fieldConfigsDict[field.Prop]?.Memory == true ? 'memory-field' : null)
            };

            if (field.Prop === FieldPropertyEnum.Number1){
              textFieldConfig.class = (hasPermissionEditFaults ? 'mydocma-ignore-readonly' : textFieldConfig.class);
            }

            config = textFieldConfig;
            // console.warn('MmFaultFieldService.getFieldConfigs.1.2')
            // console.warn(config);
            break;

          case FaultFieldTypeEnum.Selection:
            // case FaultFieldTypeEnum.Structure:

            // prefix: { icon: }

            const selectFieldConfig: FilterSelectFieldConfig = {
              id: field.Id,
              name: field.Prop,
              label: {
                text: field.Name,
                tooltip: '',
              },

              type: FieldTypeEnum.filterSelect,
              required: (field.Permission === FieldPermission.Mandatory),
              readonly: !(field.Permission === FieldPermission.Write || field.Permission === FieldPermission.Mandatory) || !hasPermissionEditFaults,
              class: (fieldConfigsDict.hasOwnProperty(field.Prop) && fieldConfigsDict[field.Prop]?.Memory == true ? 'memory-field' : null),
              removeClearButton: !(field.Permission === FieldPermission.Write || field.Permission === FieldPermission.Mandatory) || !hasPermissionEditFaults
            };

            // console.info(`field '${field.Prop}' - ${fieldConfigsDict[field.Prop]?.Memory}`);

            // if (field.Prop === FieldPropertyEnum.AttributeGroup_Image) {
            //   selectFieldConfig.prefix = ({
            //     icon: 'account_tree',
            //     readonly: true,
            //   } as FieldAffix);
            // }

            if (noDataSourceFields.every(x => x !== field.Prop) || !companyDependencyEnabled) {

              const dataSource = await this.getFieldSource(field);
              selectFieldConfig.source = dataSource;
              console.info(`field datasource '${field.Prop}': length ${dataSource?.length ?? 0}`);
            }

            config = selectFieldConfig;
            // console.warn('MmFaultFieldService.getFieldConfigs.1.3')
            // console.warn(config);
            break;

          case FaultFieldTypeEnum.Switch:
            const switchFieldConfig: SlideToggleFieldConfig = {
              id: field.Id,
              name: field.Prop,
              label: {
                text: field.Name,
                tooltip: ''
              },
              type: FieldTypeEnum.slidetoggle,
              required: (field.Permission === FieldPermission.Mandatory),
              readonly: !(field.Permission === FieldPermission.Write || field.Permission === FieldPermission.Mandatory) || !hasPermissionEditFaults,
              allowLabelCollapse: true,
              class: (fieldConfigsDict.hasOwnProperty(field.Prop) && fieldConfigsDict[field.Prop]?.Memory == true ? 'memory-field' : null)
            };
            config = switchFieldConfig;
            // console.warn('MmFaultFieldService.getFieldConfigs.1.4')
            // console.warn(config);
            break;

          case FaultFieldTypeEnum.Date:
            const dateFieldConfig: DateFieldConfig = {
              id: field.Id,
              name: field.Prop,
              label: {
                text: field.Name,
                tooltip: ''
              },
              value: null,
              type: FieldTypeEnum.date,
              required: (field.Permission === FieldPermission.Mandatory),
              readonly: !(field.Permission === FieldPermission.Write || field.Permission === FieldPermission.Mandatory) || !hasPermissionEditFaults,
              class: (fieldConfigsDict.hasOwnProperty(field.Prop) && fieldConfigsDict[field.Prop]?.Memory == true ? 'memory-field' : null)
            };
            config = dateFieldConfig;
            // console.warn('MmFaultFieldService.getFieldConfigs.1.5')
            // console.warn(config);
            break;

          case FaultFieldTypeEnum.Number:
            const numberFieldConfig: NumberFieldConfig = {
              id: field.Id,
              name: field.Prop,
              label: {
                text: field.Name,
                tooltip: ''
              },
              type: FieldTypeEnum.number,
              required: (field.Permission === FieldPermission.Mandatory),
              readonly: !(field.Permission === FieldPermission.Write || field.Permission === FieldPermission.Mandatory) || !hasPermissionEditFaults,
              clearButtonVisibility: true,
              numberType: NumberTypeEnum.currency,
              class: (fieldConfigsDict.hasOwnProperty(field.Prop) && fieldConfigsDict[field.Prop]?.Memory == true ? 'memory-field' : null)
            };

            config = numberFieldConfig;
            // console.warn('MmFaultFieldService.getFieldConfigs.1.6')
            // console.warn(config);
            break;
        }

        if (config) {
          // console.warn('MmFaultFieldService.getFieldConfigs.2 (' + formConfigs.length + ')')
          formConfigs.push(config);

          if (config2) {
            formConfigs.push(config2);
          }
        }
      }
      catch(err) {
        this.logger.error(err, '', 'MmFaultFieldService');
      }
    }

    // console.warn('MmFaultFieldService.getFieldConfigs.3 (' + formConfigs.length + ')')
    return formConfigs;
  }

  async getFieldSource(field: FaultField): Promise<SelectItem[]> {
    switch (field.Prop) {
      case FieldPropertyEnum.Craft:
        return await firstValueFrom(this.crafts$);
      case FieldPropertyEnum.Order:
        return await firstValueFrom(this.orders$);
      case FieldPropertyEnum.CostsOrder:
        return await firstValueFrom(this.costOrders$);
      case FieldPropertyEnum.Company:
        return await firstValueFrom(this.companies$);
      case FieldPropertyEnum.CostsCompany:
        return await firstValueFrom(this.costCompanies$);
      case FieldPropertyEnum.Class1:
        return await firstValueFrom(this.class1$);
      case FieldPropertyEnum.Class2:
        return await firstValueFrom(this.class2$);
      case FieldPropertyEnum.Class3:
        return await firstValueFrom(this.class3$);
      case FieldPropertyEnum.Class4:
        return await firstValueFrom(this.class4$);
      case FieldPropertyEnum.UserDefined1:
        return await firstValueFrom(this.userDefineds1$);
      case FieldPropertyEnum.UserDefined2:
        return await firstValueFrom(this.userDefineds2$);
      case FieldPropertyEnum.Structure:
        return await firstValueFrom(this.structures$);
      default:
        // console.error('MmFaultFieldService.getFieldSource.BREAK_CASE ' + field.Prop);
        break;
    }

    // if (this.fieldGroups.responsibleFields.find(r => r.Prop === field.Prop)) {
    //   return firstValueFrom(this.contacts$);
    // }

    // console.error('MmFaultFieldService.getFieldSource.NO_SOURCE_FOR ' + field.Prop);

    const emptySource: SelectItem[] = [];
    return await Promise.resolve<SelectItem[]>(emptySource);
  }

}
